import React, { useState, useEffect, useMemo } from 'react';
import useGameStore from '../store/gameStore';
import useAudioStore from '../store/audioStore';
import { useTranslation } from '../i18n';
import { DRIVERS, HERO_DRIVERS } from '../data/drivers';
import { isDriverUnlocked } from '../utils/driverRecruitment';
import { formatMoney } from '../utils/format';
import { computeVisibleSlots } from '../store/slices/driverPoolSlice';
import { DEFAULT_TEAM_FILTERS, DEFAULT_RECRUIT_FILTERS, filterTeam, filterCandidates, collectSpecialties } from '../utils/hrFilters';
import { TeamFilters, RecruitFilters } from '../components/hr/HRFilters';
import HRDashboard from '../components/game/HRDashboard';
import HRDashboardPanel from '../components/hr/HRDashboardPanel';
import HolidayPlanningPanel from '../components/hr/HolidayPlanningPanel';
import NadiaHRDashboard from '../components/hr/NadiaHRDashboard';
import DriverDetailCard from '../components/hr/DriverDetailCard';
import RecruitList from '../components/hr/RecruitList';
import FireConfirmModal from '../components/hr/FireConfirmModal';
import FormerDriversTab from '../components/hr/FormerDriversTab';
import { HireNegotiationModal } from '../components/game/HireNegotiationModal';
import { TeamCompatibilityMatrix } from '../components/strategie/TeamCompatibilityMatrix';
import RSEDashboard from '../components/rse/RSEDashboard';
import MarkdownLite from '../components/MarkdownLite';
import { UserPlus, Briefcase, BarChart3, Network, ShieldCheck, Sparkles, ArchiveX, AlertTriangle } from 'lucide-react';

/**
 * HR — page orchestrator. Three tabs: team (driver detail cards),
 * progression (HR dashboards), recruit (candidate list + negotiation
 * modal). Each tab content lives in a dedicated component under
 * `components/hr/` (DriverDetailCard, RecruitList, HRDashboardPanel).
 *
 * Refactored 28/05/2026 from an 836-line monolith. Now ~135 lines:
 * just unlock gating, candidate filtering and tab orchestration.
 */
export default function HR() {
  const [tab, setTab] = useState('team');
  const [negotiationTarget, setNegotiationTarget] = useState(null);
  // 02/06/2026 — Confirmation modal for fire/rupture. Stores the driverId
  // currently being terminated so we can render the modal once.
  const [firingDriver, setFiringDriver] = useState(null);
  const {
    drivers, vehicles, treasury, totalDays, monthlyRevenues, activeContracts,
    hireDriver: _hire, fireDriver, giveBonus, forceRest, trainDriver, unassignDriver,
    sendDriverOnHoliday,
    buyBackHolidayDays,
    hiredSupport,
    firedDriversArchive,
  } = useGameStore();
  const playSound = useAudioStore((s) => s.play);
  const { t } = useTranslation();
  // 28/07/2026 — Recrutement local déplacé vers Management des dépôts
  // (onglet dédié, débloqué par un dépôt physique dans le pays concerné).

  // Personality "daily mood" insights are gated behind hiring Nadia Cheikh
  // (Responsable RH, role-nadia-cheikh). Until she's on payroll the player
  // sees only the trait labels, not the per-mission like/dislike effects.
  const hrInsightsUnlocked = !!(hiredSupport || {})['role-nadia-cheikh'];
  const rhTier1Unlocked = hrInsightsUnlocked;
  const rhTier2Unlocked = !!(hiredSupport || {})['role-karim-sefrioui'];

  const hiredIds = drivers.map(d => d.dataId);
  const negotiations = useGameStore.getState().driverNegotiations || {};

  // ── Phase R1 (04/02/2026) — Vivier RH dynamique ─────────────────────────
  // Au lieu de filtrer toute la base DRIVERS, on s'appuie désormais sur
  // `driverPool.availableIds` (slice driverPoolSlice). Le pool est
  // initialisé au premier rendu si vide.
  const driverPool = useGameStore((s) => s.driverPool) || { availableIds: [], takenBy: {} };
  const initDriverPool = useGameStore((s) => s.initDriverPool);
  useEffect(() => {
    if (!driverPool.availableIds || driverPool.availableIds.length === 0) {
      if (typeof initDriverPool === 'function') initDriverPool();
    }
  }, []);

  // 16/06/2026 — Règles de déblocage extraites dans utils/driverRecruitment
  // (bug : les profils élites « Réputation > 85… » ne matchaient aucune
  // branche et restaient invisibles à jamais — frères Lumery inclus).
  const unlockCtx = { rhTier1Unlocked, rhTier2Unlocked, vehicles, monthlyRevenues, totalDays };
  // Profils visibles = profils du vivier (héros + marché en circulation)
  // bornés au nombre de slots calculés (BASE + bonus supports/site/annonce).
  const visibleSlots = computeVisibleSlots(useGameStore.getState());
  const poolIds = driverPool.availableIds || [];
  const visibleIds = poolIds.slice(0, visibleSlots);
  const takenBy = driverPool.takenBy || {};
  // Décompose en 3 groupes : disponibles, pris par concurrent, verrouillés
  // par déblocage RH/CA. Les chauffeurs « pris par concurrent » restent
  // visibles avec un badge (choix utilisateur 3b).
  const poolDrivers = visibleIds
    .map(id => DRIVERS.find(d => d.id === id))
    .filter(Boolean)
    .filter(d => !hiredIds.includes(d.id));
  const availableDrivers = poolDrivers.filter(d => isDriverUnlocked(d, unlockCtx) && !takenBy[d.id]);
  const takenDrivers = poolDrivers.filter(d => isDriverUnlocked(d, unlockCtx) && takenBy[d.id])
    .map(d => ({ ...d, takenByCompetitor: takenBy[d.id] }));
  const lockedDrivers = poolDrivers.filter(d => !isDriverUnlocked(d, unlockCtx));

  // KPI summary stats
  const activeDrivers = drivers.filter(d => d.status === 'active').length;
  const trainingDrivers = drivers.filter(d => d.status === 'training').length;
  const totalHRCost = drivers.reduce((sum, d) => sum + d.employerCost, 0);

  // 18/09/2026 (REX apprenant) — filtres Équipe (permis / niveau / dispo) et
  // Recrutement (coût employeur / niveau / spécialité). Cf. utils/hrFilters.js.
  const [teamFilters, setTeamFilters] = useState(DEFAULT_TEAM_FILTERS);
  const [recruitFilters, setRecruitFilters] = useState(DEFAULT_RECRUIT_FILTERS);
  const filteredTeam = useMemo(() => filterTeam(drivers, teamFilters), [drivers, teamFilters]);
  const filteredCandidates = useMemo(() => filterCandidates(availableDrivers, recruitFilters), [availableDrivers, recruitFilters]);
  const candidateSpecialties = useMemo(() => collectSpecialties(availableDrivers), [availableDrivers]);

  return (
    <div className="space-y-4" data-testid="hr-page">
      <h2 className="text-2xl font-bold text-foreground">{t('hr_title')}</h2>

      {/* HR Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('headcount')}</p>
          <p className="text-xl font-bold text-foreground">{drivers.length}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('on_mission')}</p>
          <p className="text-xl font-bold text-money">{activeDrivers}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('in_training')}</p>
          <p className="text-xl font-bold text-accent">{trainingDrivers}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('payroll')}</p>
          <p className="text-lg font-bold text-danger">{formatMoney(totalHRCost)}/m</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setTab('team')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'team' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-team"
        >
          {t('team_tab')} ({drivers.length})
        </button>
        <button
          onClick={() => setTab('progression')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'progression' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-progression"
        >
          <BarChart3 className="w-4 h-4 inline mr-1" /> Progression
        </button>
        <button
          onClick={() => setTab('recruit')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'recruit' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-recruit"
        >
          <UserPlus className="w-4 h-4 inline mr-1" /> {t('recruit_tab')} ({availableDrivers.length})
        </button>
        {/* 02/06/2026 — Moved from Stratégie : Compatibility matrix is a RH tool. */}
        <button
          onClick={() => setTab('compat')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'compat' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-compat"
        >
          <Network className="w-4 h-4 inline mr-1" /> {t('hr_tab_compat')}
        </button>
        {/* 02/06/2026 — Moved from Stratégie : HSE/RSE belongs to RH (Claire Maréchal). */}
        <button
          onClick={() => setTab('rse')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'rse' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-rse"
        >
          <ShieldCheck className="w-4 h-4 inline mr-1" /> {t('hr_tab_rse')}
        </button>
        {/* 08/02/2026 — Q2 Future/Backlog : Anciens chauffeurs (archive). */}
        <button
          onClick={() => setTab('former')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'former' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-former"
        >
          <ArchiveX className="w-4 h-4 inline mr-1" /> {t('hr_tab_former')} ({Object.keys(firedDriversArchive || {}).filter(k => !drivers.find(d => d.dataId === k)).length})
        </button>
      </div>

      {/* Team */}
      {tab === 'team' && (
        <div className="space-y-3">
          {/* 10/02/2026 (Lot M) — Panneau de planification des congés.
              Conseil Nadia + bouton « Envoyer » par chauffeur priorisé. */}
          <HolidayPlanningPanel />
          {/* 09/02/2026 — Q2 Lot E user prod : bannière préavis. Le bug
              critique remonté ("chauffeurs disparaissent sans raison")
              vient des préavis non visibles. On affiche maintenant un
              encart dédié en tête de l'onglet Équipe avec la liste et
              le nombre de jours restants. */}
          {drivers.some(d => d.pendingDeparture) && (
            <div
              className="bg-rose-500/10 border border-rose-400/50 rounded-md px-3 py-2 flex items-start gap-2"
              data-testid="hr-pending-departure-banner"
            >
              <AlertTriangle className="w-4 h-4 text-rose-300 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-rose-100 leading-snug flex-1">
                <p className="font-bold mb-0.5">
                  {drivers.filter(d => d.pendingDeparture).length} chauffeur(s) en préavis de départ
                </p>
                <p className="text-rose-200/80">
                  Sans intervention, ils quitteront l'entreprise à expiration du préavis.
                  Consultez la <strong>Messagerie</strong> pour aligner leur salaire (dernière chance) :
                </p>
                <ul className="mt-1 space-y-0.5">
                  {drivers.filter(d => d.pendingDeparture).map(d => (
                    <li key={d.id} className="font-mono text-[10px]">
                      • <span className="font-bold">{d.name}</span> — départ J-{Math.max(0, (d.pendingDeparture.noticeUntil || 0) - (totalDays || 0))}
                      {d.pendingDeparture.expectedRaise ? ` (attente : +${d.pendingDeparture.expectedRaise} €/mois)` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {!hrInsightsUnlocked && drivers.length > 0 && (
            <div className="bg-accent/5 border border-accent/30 rounded-md px-3 py-2 flex items-start gap-2" data-testid="hr-insights-hint">
              <Briefcase className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-slate-300 leading-snug">
                {t('hr_insights_hint_prefix')} <span className="font-semibold text-accent">{t('hr_insights_hint_role')}</span> {t('hr_insights_hint_mid')} <span className="text-money font-medium">{t('hr_insights_hint_likes')}</span> {t('hr_insights_hint_or')} <span className="text-danger font-medium">{t('hr_insights_hint_dislikes')}</span> {t('hr_insights_hint_suffix')}
              </div>
            </div>
          )}
          {drivers.length === 0 ? (
            <div className="bg-ink-800 rounded-lg border border-ink-600 p-8 text-center">
              <UserPlus className="w-12 h-12 mx-auto text-slate-600 mb-3" />
              <p className="text-slate-500">{t('no_driver')}</p>
              <button
                onClick={() => setTab('recruit')}
                className="mt-3 text-sm bg-accent text-white px-4 py-2 rounded-lg font-medium hover:bg-accent-hover transition-colors"
                data-testid="go-recruit-btn"
              >
                {t('recruit_now')}
              </button>
            </div>
          ) : (
            <>
              <TeamFilters t={t} filters={teamFilters} onChange={setTeamFilters} shown={filteredTeam.length} total={drivers.length} />
              {filteredTeam.length === 0 && (
                <div className="bg-ink-800 rounded-lg border border-ink-600 p-6 text-center text-sm text-slate-500" data-testid="team-filter-empty">
                  {t('hr_filter_empty_team')}
                </div>
              )}
              {filteredTeam.map(d => (
              <DriverDetailCard
                key={d.id}
                driver={d}
                vehicles={vehicles}
                activeContracts={activeContracts}
                treasury={treasury}
                totalDays={totalDays}
                onRest={forceRest}
                onBonus={giveBonus}
                onFire={(driverId) => {
                  const d = drivers.find(x => x.id === driverId);
                  if (d) setFiringDriver(d);
                }}
                onTrain={(driverId, trainingId) => {
                  playSound('training');
                  // 17/06/2026 — Feedback utilisateur explicite si l'action
                  // échoue (avant : silencieux → bouton sans effet, cas
                  // Thibaut Renard chauffeur racheté en mission).
                  const res = trainDriver(driverId, trainingId);
                  if (res && !res.ok) {
                    const msg = res.reason === 'on_mission'
                      ? 'Impossible : ce chauffeur est en mission. Détachez-le d\'abord (« Désaffecter »).' /* i18n-keep */
                      : res.reason === 'busy'
                        ? 'Ce chauffeur est déjà en formation.' /* i18n-keep */
                        : res.reason === 'personality_refuse'
                          ? `Refuse cette formation : ${res.label || res.trait || 'incompatibilité'}.` /* i18n-keep */
                          : res.reason === 'insufficient_funds'
                            ? 'Trésorerie insuffisante pour cette formation.' /* i18n-keep */
                            : res.reason === 'spec_already_owned'
                              ? 'Spécialité déjà acquise.' /* i18n-keep */
                              : `Formation impossible (${res.reason || 'erreur inconnue'}).`; /* i18n-keep */
                    alert(msg); /* i18n-keep */
                  }
                }}
                onUnassign={unassignDriver}
                onSendOnHoliday={(driverId) => {
                  const res = sendDriverOnHoliday && sendDriverOnHoliday(driverId);
                  if (res && !res.ok && res.reason === 'insufficient_balance') {
                    // i18n-keep — short transient confirmation alert, kept in FR
                    // to match the rest of the HR domain copy (no en mirror yet).
                    alert("Solde de congés insuffisant (5 jours requis pour un bloc)."); /* i18n-keep */
                  }
                }}
                onBuyBackHolidays={(driverId) => {
                  const res = buyBackHolidayDays && buyBackHolidayDays(driverId);
                  if (res && !res.ok) {
                    const msg = res.reason === 'yearly_cap_reached'
                      ? 'Plafond CET atteint : 10 jours rachetés maximum par chauffeur et par an.' /* i18n-keep */
                      : res.reason === 'insufficient_treasury'
                        ? `Trésorerie insuffisante pour l'indemnité CET (${(res.cost || 0).toLocaleString('fr-FR')} € requis).` /* i18n-keep */
                        : 'Rachat impossible (solde de congés < 5 jours).'; /* i18n-keep */
                    alert(msg);
                  }
                }}
                t={t}
                hrInsightsUnlocked={hrInsightsUnlocked}
              />
              ))}
            </>
          )}
        </div>
      )}

      {/* Progression Dashboard */}
      {tab === 'progression' && (
        <div className="space-y-4">
          <NadiaHRDashboard drivers={drivers} hiredSupport={hiredSupport} />
          <HRDashboardPanel />
          <HRDashboard drivers={drivers} vehicles={vehicles} totalDays={totalDays} />
        </div>
      )}

      {/* Recruit */}
      {tab === 'recruit' && (
        <>
          {availableDrivers.length > 0 && (
            <RecruitFilters
              t={t}
              filters={recruitFilters}
              onChange={setRecruitFilters}
              specialties={candidateSpecialties}
              shown={filteredCandidates.length}
              total={availableDrivers.length}
            />
          )}
          {availableDrivers.length > 0 && filteredCandidates.length === 0 && (
            <div className="bg-ink-800 rounded-lg border border-ink-600 p-6 text-center text-sm text-slate-500" data-testid="recruit-filter-empty">
              {t('hr_filter_empty_recruit')}
            </div>
          )}
          <RecruitList
            availableDrivers={filteredCandidates}
            hideEmptyState={availableDrivers.length > 0}
            takenDrivers={takenDrivers}
            lockedDrivers={lockedDrivers}
            negotiations={negotiations}
            totalDays={totalDays}
            onPropose={(d) => { playSound('hire'); setNegotiationTarget(d); }}
            t={t}
          />
        </>
      )}

      {/* 02/06/2026 — Compatibility matrix tab (Nadia Cheikh gate) */}
      {tab === 'compat' && (
        hrInsightsUnlocked ? (
          <TeamCompatibilityMatrix
            drivers={drivers}
            vehicles={vehicles}
            activeContracts={activeContracts}
          />
        ) : (
          <div className="surface-card p-4 border-dashed" data-testid="compat-locked">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-300">{t('strat_compat_title')}</p>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                  <MarkdownLite text={t('strat_compat_lock_msg')} />
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* 02/06/2026 — RSE / HSE tab (Claire Maréchal gate inside the component) */}
      {tab === 'rse' && (
        <RSEDashboard />
      )}

      {/* 08/02/2026 — Q2 Future/Backlog : anciens chauffeurs archivés. */}
      {tab === 'former' && (
        <FormerDriversTab
          archive={firedDriversArchive}
          hiredIds={hiredIds}
          treasury={treasury}
          onRehire={(dataId) => {
            playSound('hire');
            _hire(dataId);
          }}
          t={t}
        />
      )}

      {negotiationTarget && (
        <HireNegotiationModal
          driver={negotiationTarget}
          onClose={() => setNegotiationTarget(null)}
          onAccept={() => setNegotiationTarget(null)}
        />
      )}

      {firingDriver && (
        <FireConfirmModal
          driver={firingDriver}
          totalDays={totalDays}
          onCancel={() => setFiringDriver(null)}
          onConfirm={({ mode }) => {
            const res = fireDriver(firingDriver.id, { mode });
            setFiringDriver(null);
            if (res && !res.ok && res.reason === 'on_mission') {
              // Notification already pushed by the action; nothing else to do.
            }
          }}
        />
      )}
    </div>
  );
}
